import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import VisitorCounter from './VisitorCounter';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const services = [
    { name: 'GST Services', path: '/services/gst' },
    { name: 'Income Tax', path: '/services/income-tax' },
    { name: 'Audit & Assurance', path: '/services/audit' },
    { name: 'Business Advisory', path: '/services/advisory' }
  ];

  return (
    <footer className="footer">
      <div className="footer-container">
        <div className="footer-grid">
          {/* Company Info */}
          <div className="footer-section">
            <div className="footer-logo">
              <img 
                src="https://customer-assets.emergentagent.com/job_ca-piyush-gupta/artifacts/yae02nne_CA%20India%20Logo-%20-%20Copy.jpg" 
                alt="CA India Logo" 
                className="footer-logo-img"
              />
              <div>
                <h3 className="footer-logo-title">CA Piyush Gupta</h3>
                <p className="footer-logo-subtitle">Gupta Piyush & Company</p>
              </div>
            </div>
            <p className="footer-description">
              A professionally managed Chartered Accountancy firm offering reliable and 
              result-oriented financial solutions.
            </p>
          </div>

          {/* Quick Links */}
          <div className="footer-section">
            <h4 className="footer-heading">Quick Links</h4>
            <ul className="footer-links">
              <li><Link to="/" className="footer-link">Home</Link></li>
              <li><Link to="/about" className="footer-link">About Us</Link></li>
              <li><Link to="/services" className="footer-link">Services</Link></li>
              <li><Link to="/contact" className="footer-link">Contact</Link></li>
            </ul>
          </div>

          {/* Services */}
          <div className="footer-section">
            <h4 className="footer-heading">Our Services</h4>
            <ul className="footer-links">
              {services.map((service) => (
                <li key={service.path}>
                  <Link to={service.path} className="footer-link">{service.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="footer-section">
            <h4 className="footer-heading">Contact Us</h4>
            <ul className="footer-contact">
              <li className="footer-contact-item">
                <MapPin size={18} className="footer-icon" />
                <span>Shop No. 3-4, First Floor, Gayatri Complex, Ansari Road, Muzaffarnagar, Uttar Pradesh - 251001</span>
              </li>
              <li className="footer-contact-item">
                <Phone size={18} className="footer-icon" />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <a href="tel:+916396550622" className="footer-contact-link">
                    +91-6396550622
                  </a>
                  <a href="tel:+917417215103" className="footer-contact-link">
                    +91-7417215103
                  </a>
                </div>
              </li>
              <li className="footer-contact-item">
                <Mail size={18} className="footer-icon" />
                <a href="mailto:cag.piyush@gmail.com" className="footer-contact-link">
                  cag.piyush@gmail.com
                </a>
              </li>
              <li className="footer-contact-item">
                <Clock size={18} className="footer-icon" />
                <span>Mon - Sat: 10:00 AM - 6:00 PM</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Social Media & Links */}
        <div className="footer-social-section">
          <h4 className="footer-social-title">Connect With Us</h4>
          <div className="footer-social-links">
            <a 
              href="https://www.facebook.com/share/17TxrCuH49/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="social-link facebook"
              aria-label="Facebook"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>
            
            <a 
              href="https://www.instagram.com/cag.piyush?igsh=MWZrcGJtMDNuNW5wcw==" 
              target="_blank" 
              rel="noopener noreferrer"
              className="social-link instagram"
              aria-label="Instagram"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </a>
            
            <a 
              href="https://share.google/SFdGZEmEGjfMHRPd8" 
              target="_blank" 
              rel="noopener noreferrer"
              className="social-link google"
              aria-label="Google"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
            </a>
            
            <a 
              href="https://www.justdial.com/Muzaffarnagar/Ca-Piyush-Gupta-Opposite-Lic-Building-Civil-Lines/9999PX131-X131-200125154028-B8C7_BZDET" 
              target="_blank" 
              rel="noopener noreferrer"
              className="social-link justdial"
              aria-label="Just Dial"
              data-testid="social-justdial"
            >
              {/* JustDial Icon - Phone with J */}
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
                <path d="M9.5 7.5v2.5h1.5v-2.5c0-.83.67-1.5 1.5-1.5h2v6.5h-2v1.5h3.5v-9.5h-3.5c-1.66 0-3 1.34-3 3z"/>
                <path d="M12.5 15.5h-1.5v2h3v-2h-1.5z"/>
                <circle cx="12" cy="12" r="1.5"/>
              </svg>
            </a>
            
            <a 
              href="https://taxguru.in/author/gpcpiyush/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="social-link taxguru"
              aria-label="TaxGuru"
              data-testid="social-taxguru"
            >
              {/* TaxGuru Icon - T with Book/Document */}
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 18H6V4h12v16z"/>
                <path d="M8 6h8v2H8V6z"/>
                <path d="M11 10h2v8h-2v-8z"/>
                <path d="M8 10h6v2H8v-2z"/>
              </svg>
            </a>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="footer-bottom">
          <p className="footer-copyright">
            © {currentYear} CA Piyush Gupta | Gupta Piyush & Company. All rights reserved.
          </p>
          <p className="footer-tagline">
            Trusted Financial Partner for Your Success
          </p>
        </div>

        {/* Visitor Counter */}
        <VisitorCounter />
      </div>
    </footer>
  );
};

export default Footer;
